import ActionTextIcon from "@/components/icons/ActionTextIcon";
import AntIcon from "@/components/icons/AntIcon";
import InfoIcon from "@/components/icons/InfoIcon";
import PopconfirmDeleteIcon from "@/components/icons/PopconfirmDeleteIcon";
import Ellipsis from "@/components/views/Ellipsis";
import { paginationTableProps, tableStandardProps } from "@/utils/componentUtils";
import { formatLocaleDate } from "@/utils/formatUtils";
import { useFilterSearchParams } from "@/utils/hooksUtils";
import { URLQuery } from "@/utils/queryUtils";
import { cssVariables, numberOrZero } from "@/utils/utils";
import { Button, Divider, Popconfirm, Table } from "antd";
import { ColumnsType } from "antd/lib/table";
import dayjs from "dayjs";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router";
import { useDeleteAgentLicense, useGetAgentLicenses } from "../../../../common/api/queries";
import { PageSizes } from "../../../../common/constants";
import type { UUID } from "../../../../typings/global";
import { AgentLicense } from "../../types";
import AgentLicenseForm from "../forms/AgentLicenseForm";

const today = dayjs();

interface Props {
  agentId: UUID;
  defaultLicenseValue?: string;
  showAllColumns?: boolean;
  showActions?: boolean;
}

export const AgentLicenseTable = ({ agentId, defaultLicenseValue, showAllColumns, showActions }: Props) => {
  const { t } = useTranslation();

  const navigate = useNavigate();

  const [licenseFormOpen, setLicenseFormOpen] = useState<boolean>(false);
  const [licenseToUpdate, setLicenseToUpdate] = useState<AgentLicense>();

  const searchParams = useFilterSearchParams();

  const { data: licenses } = useGetAgentLicenses(agentId);
  const deleteAgentLicense = useDeleteAgentLicense();

  const handleLicenseUpdateClick = (license: AgentLicense) => {
    setLicenseToUpdate(license);
    setLicenseFormOpen(true);
  };

  const handleLicenseFormCancel = () => {
    setLicenseToUpdate(undefined);
    setLicenseFormOpen(false);
  };

  const columns: ColumnsType<AgentLicense> = [
    {
      key: "sector",
      title: t("agent.enums.financialSector._label"),
      width: 170,
      ellipsis: { showTitle: false },
      render: (_, record) => <Ellipsis>{t("agent.enums.financialSector." + record.sector)}</Ellipsis>,
    },
    {
      key: "value",
      title: t("agent.license.attrs.value"),
      ellipsis: { showTitle: false },
      render: (_, record) => <Ellipsis>{record.value}</Ellipsis>,
    },
    {
      key: "registrationProposalDate",
      title: t("agent.license.helpers.registrationProposalDateShort"),
      width: 100,
      render: (_, record) => formatLocaleDate(record.registrationProposalDate),
    },
    {
      key: "registrationDate",
      title: t("agent.license.helpers.registrationDateShort"),
      width: 100,
      render: (_, record) => (record.registrationDate ? formatLocaleDate(record.registrationDate) : undefined),
    },
    {
      key: "deletionProposalDate",
      title: t("agent.license.helpers.deletionProposalDateShort"),
      width: 100,
      render: (_, record) => (record.deletionProposalDate ? formatLocaleDate(record.deletionProposalDate) : undefined),
    },
    {
      key: "deletionDate",
      title: t("agent.license.helpers.deletionDateShort"),
      width: 100,
      render: (_, record) => (record.deletionDate ? formatLocaleDate(record.deletionDate) : undefined),
    },
    {
      key: "active",
      title: t("agent.license.helpers.active"),
      align: "center",
      width: 70,
      render: (_, record) =>
        (record.registrationDate || record.registrationProposalDate
          ? today.isSameOrAfter(record.registrationDate || record.registrationProposalDate, "day")
          : true) &&
        (record.deletionDate || record.deletionProposalDate
          ? today.isBefore(record.deletionDate || record.deletionProposalDate, "day")
          : true) ? (
          <AntIcon type="check-circle-two-tone" twoToneColor={cssVariables.colorGreen} />
        ) : (
          <AntIcon type="close-circle-two-tone" twoToneColor={cssVariables.colorRed} />
        ),
    },
  ];

  if (showAllColumns) {
    columns.push({
      key: "note",
      align: "center",
      width: 40,
      render: (_, record) =>
        record.note ? <InfoIcon title={t("agent.license.attrs.note")} tooltip={record.note} /> : null,
    });

    if (showActions) {
      columns.push({
        key: "actions",
        align: "right",
        fixed: "right",
        width: 180,
        render: (_, record) => (
          <>
            <ActionTextIcon
              icon="edit"
              color="blue"
              text={t("common.edit")}
              onClick={() => handleLicenseUpdateClick(record)}
            />

            <Divider orientation="vertical" />

            <Popconfirm
              title={t("agent.license.helpers.deleteConfirm")}
              icon={<PopconfirmDeleteIcon />}
              okText={t("common.yes")}
              cancelText={t("common.no")}
              okType="danger"
              onConfirm={() => deleteAgentLicense.mutate({ id1: agentId, id2: record.id })}
            >
              <ActionTextIcon icon="delete" color="red" text={t("common.delete")} />
            </Popconfirm>
          </>
        ),
      });
    }
  }

  return (
    <>
      <Table<AgentLicense>
        {...tableStandardProps()}
        columns={columns}
        dataSource={licenses}
        pagination={{
          ...paginationTableProps,
          current: numberOrZero(searchParams.pageIndex) + 1,
          pageSize: PageSizes.LARGE,
          total: licenses?.length,
          onChange: (pageIndex) => navigate({ search: URLQuery.getTableChangePageIndex(pageIndex) }, { replace: true }),
        }}
      />

      {showActions && (
        <>
          <div className={licenses?.length ? "table-footer-margin" : "margin-top-small"}>
            <Button type="primary" size="small" icon={<AntIcon type="plus" />} onClick={() => setLicenseFormOpen(true)}>
              {t("common.add")}
            </Button>
          </div>

          <AgentLicenseForm
            open={licenseFormOpen}
            agentId={agentId}
            defaultLicenseValue={defaultLicenseValue}
            license={licenseToUpdate}
            onFormCancel={handleLicenseFormCancel}
          />
        </>
      )}
    </>
  );
};
